import { useTranslation } from 'react-i18next';
import {
  IBudget,
  IEmployeeBudget,
} from '@/common/integrations/menhir/employee/budgets/budgets.types';
import {
  MoneyUtils,
  defaultMoney,
  moneyToString,
} from '@coverflex-tech/hypernova-utils';
import { DateUtils, formatDate } from './date';

export function shouldTreatBudgetAsExpired(
  employeeBudget: IEmployeeBudget
): boolean {
  return (
    employeeBudget.status === 'expired' || employeeBudget.status === 'removed'
  );
}

export function getIsBudgetAvailableSoon(
  employeeBudget: IEmployeeBudget
): boolean {
  return (
    employeeBudget.status === 'active' &&
    employeeBudget.firstTopUpAlreadyBeenMade === false &&
    [undefined, true].includes(
      employeeBudget.applicationTemplate?.singleApplication
    )
  );
}

export function useGetBudgetCardDescription(
  employeeBudget: IEmployeeBudget,
  withCummulativeKey = true
): string {
  const { t } = useTranslation();

  if (employeeBudget.status === 'active' && employeeBudget.budget.recurrence) {
    const cumulativeKey = employeeBudget.budget.withAccrual
      ? 'dashboard.budgets.details.cumulative'
      : 'dashboard.budgets.details.non_cumulative';
    const cumulativeText =
      employeeBudget.budget.recurrence === 'one_time' || !withCummulativeKey
        ? ''
        : `, ${t(cumulativeKey)}`;

    const amountString = (() => {
      if (employeeBudget.budget.recurrence === 'one_time') {
        return withCummulativeKey
          ? `${MoneyUtils.moneyToString(employeeBudget.budgetAmount)}, `
          : '';
      } else {
        return `${MoneyUtils.moneyToString(employeeBudget.budgetAmount)} `;
      }
    })();

    return [
      amountString,
      t(
        `dashboard.budgets.details.card.recurrence.${employeeBudget.budget.recurrence}`
      ),
      cumulativeText,
    ].join('');
  }
  return '';
}

export function useGetBudgetCardRecurrenceText({
  employeeBudget,
  isAvailableSoon,
}: {
  employeeBudget: IEmployeeBudget;
  isAvailableSoon: boolean;
}) {
  const { t } = useTranslation();
  const isExpired = shouldTreatBudgetAsExpired(employeeBudget);

  if (isAvailableSoon) {
    return t('dashboard.budgets.badge_description', {
      date: formatDate(employeeBudget.firstTopUpDate || ''),
    });
  }

  if (isExpired && !!employeeBudget.endedAt) {
    return t('dashboard.budgets.expired_on', {
      date: DateUtils.formatDate(employeeBudget.endedAt),
    });
  }

  // active and recurrent -> renews on x
  if (
    employeeBudget.status === 'active' &&
    employeeBudget.budget.recurrence !== 'one_time' &&
    !!employeeBudget.budget.renewalDate
  ) {
    return t('dashboard.budgets.renews_on', {
      date: DateUtils.formatDate(employeeBudget.budget.renewalDate),
    });
  }

  // one time and ended -> expires on x
  if (
    employeeBudget.status === 'active' &&
    (!!employeeBudget.endedAt || !!employeeBudget.budget.endedAt)
  ) {
    return t('dashboard.budgets.expires_on', {
      date: DateUtils.formatDate(
        employeeBudget.endedAt ?? employeeBudget.budget.endedAt
      ),
    });
  }
  // TODO: cover the case where the budget will expire before it renews

  return '';
}

export function getPercentageOfBudgetUsed(
  budgetEmployee: IEmployeeBudget
): number {
  if (
    !budgetEmployee.pocket ||
    !budgetEmployee.spentAmount ||
    budgetEmployee.spentAmount.amount < 0
  ) {
    return 0;
  }

  if (budgetEmployee.budget.paymentMethod === 'payroll') return 100;

  const totalAmount = budgetEmployee.budget.withAccrual
    ? (budgetEmployee.accumulatedAmount ?? defaultMoney)
    : budgetEmployee.budgetAmount;

  const percent =
    (budgetEmployee.spentAmount.amount / totalAmount.amount) * 100;
  return percent > 100 ? 100 : percent;
}

export const useGetBudgetCircleContent = ({
  employeeBudget,
  isPayRoll,
  isAvailableSoon,
  isSpent,
}: {
  employeeBudget: IEmployeeBudget;
  isPayRoll: boolean;
  isAvailableSoon: boolean;
  isSpent: boolean;
}) => {
  const { t } = useTranslation();

  if (isPayRoll) {
    return {
      title: moneyToString(employeeBudget.budgetAmount),
      description: t('dashboard.budgets.assigned'),
    };
  }

  if (isAvailableSoon) {
    return {
      title: moneyToString(employeeBudget.budgetAmount),
      description: t('dashboard.budgets.soon'),
    };
  }

  if (isSpent) {
    return {
      title: t('dashboard.budgets.spent'),
    };
  }

  return {
    title: moneyToString(employeeBudget.pocket?.availableBalance),
    description: t('dashboard.budgets.available'),
  };
};

export function getBudgetReimbursementKey(budget: IBudget) {
  if (budget.paymentMethod === 'childcare') {
    return 'dashboard.budgets.details.learning_centre.childcare_reimbursement_description';
  }
  if (budget.paymentMethod === 'payroll') {
    return 'dashboard.budgets.details.learning_centre.payroll_reimbursement_description';
  }
  return 'dashboard.budgets.details.learning_centre.reimbursement_description';
}
